import React, { ComponentType, useState } from 'react'
import { Box, Button, Grid, Typography } from '@mui/material'
import { AxiosError, AxiosResponse } from 'axios'
import isEmpty from 'lodash/isEmpty'
import dayjs from 'dayjs'
import { UseMutationResult } from '@tanstack/react-query'

import { useDialog } from '@/hooks'
import { RadioGroup } from '@/components/radio-group.tsx'
import { Dialog, useSnackbarContext } from '@/components'
import { Form } from '@/components/form'
import { ApiErrorResponse, ReportFormat, ReportParams, SbpReportParams } from '@/types.ts'
import { downloadFile } from '@/utils.ts'

export type ReportRequestFormState = ReportParams

const REPORT_FORM = 'b2cReportForm'

export interface ReportDialogProps<FormType> {
  initialRange?: [string?, string?]
  renderComponent: ComponentType<{ initialRange?: [string?, string?]; onChange: (data: FormType) => void }>
  mutation: UseMutationResult<AxiosResponse<Blob>, unknown, Record<string, unknown>, unknown>
}
export function B2CReportDialog<FormType extends { dateTo?: string; dateFrom?: string }>({
  mutation,
  initialRange,
  renderComponent: RenderComponent,
}: ReportDialogProps<FormType>): React.ReactElement {
  const [data, setData] = useState<FormType>({ dateTo: initialRange?.[0], dateFrom: initialRange?.[1] } as FormType)
  const [format, setFormat] = useState<ReportFormat>('xlsx')
  const { showDialog, handleShowDialog, handleHideDialog } = useDialog()
  const { mutateAsync, reset } = mutation
  const { addSnack } = useSnackbarContext()

  const handleFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormat(event.target.value as SbpReportParams['formatReport'])
  }

  const handleReportSubmit = async () => {
    try {
      const response = await mutateAsync({
        ...data,
        dateTo: dayjs(data.dateTo, 'YYYY-MM-DD').format(`DD.MM.YYYY`),
        dateFrom: dayjs(data.dateFrom, 'YYYY-MM-DD').format(`DD.MM.YYYY`),
        formatReport: format,
      })

      if (response?.status === 200 && response.data) {
        const filename = response?.headers['content-disposition']?.match(/filename="([^"]+)"/i)

        downloadFile(URL.createObjectURL(response.data), filename?.[1] ?? `report_${data.dateFrom}-${data.dateTo}`)
        handleHideDialog()
        reset()
        setData({} as FormType)
      }

      if (response?.status !== 200) {
        addSnack({
          severity: 'error',
          message: 'Произошла ошибка при загрузке файла',
        })
      }
    } catch (axiosError) {
      let errorMessage = 'Произошла непредвиденная ошибка'

      const error = axiosError as AxiosError<ApiErrorResponse>
      if (error.isAxiosError && error.response?.data?.errorDetails) {
        errorMessage = error.response.data.errorDetails
      }

      addSnack({
        message: errorMessage,
        severity: 'error',
        timeout: 3000,
      })
    }
  }

  const disabledSubmit = isEmpty(data)

  return (
    <Box display="flex" justifyContent="flex-end">
      <Button onClick={handleShowDialog} variant="outlined">
        Выгрузить данные за период
      </Button>
      {showDialog && (
        <Form<ReportRequestFormState>
          name={REPORT_FORM}
          initialValues={{
            formatReport: 'xlsx',
            dateFrom: '',
            dateTo: '',
          }}
          onSubmit={handleReportSubmit}
        >
          <Dialog
            title="Выгрузить данные за период"
            open={showDialog}
            onClose={handleHideDialog}
            content={
              <Grid container flexDirection="column" spacing={2}>
                <Grid item>
                  <Typography variant="body1" color="text.secondary">
                    Период отчёта
                  </Typography>
                </Grid>
                <Grid item>
                  <RenderComponent initialRange={[data.dateFrom, data.dateTo]} onChange={setData} />
                </Grid>
                <Grid item>
                  <RadioGroup
                    row
                    value={format}
                    onChange={handleFormatChange}
                    label="Формат отчёта"
                    items={[
                      { title: '.xlsx', value: 'xlsx' },
                      { title: '.csv', value: 'csv' },
                    ]}
                  />
                </Grid>
              </Grid>
            }
            actions={
              <Button disabled={disabledSubmit} fullWidth onClick={handleReportSubmit}>
                Отправить заявку
              </Button>
            }
          />
        </Form>
      )}
    </Box>
  )
}
